import { ErrorType, Result } from "@bridgesplit/utils";
import { Commitment } from "@solana/web3.js";

import { TransactionWithIdentifier, OrderedTransactions } from "../types";

export type TransactionCombineOptions = {
    ignoreFails?: boolean;
    order: "parallel" | "sequential" | "multiStep";
    commitmentLevel?: Commitment;
};

const MAX_SEQUENTIAL_TRANSACTIONS = 10;
export async function combineTransactionPromises(
    transactions: Promise<Result<TransactionWithIdentifier[]>>[],
    options: TransactionCombineOptions
): Promise<Result<OrderedTransactions>> {
    try {
        const generatedTxns = await Promise.all(transactions);

        const fail = generatedTxns.find((t) => !t.isOk());
        if (fail && !options.ignoreFails) {
            return Result.err(fail);
        }

        const validTransactions = generatedTxns.filter((t) => t.isOk());
        if (!validTransactions.length) {
            return Result.errFromMessage("Unable to generate any transactions", {
                errorType: ErrorType.TransactionSendError
            });
        }

        const unwrapped = validTransactions.map((t) => t.unwrap());
        if (options.order === "parallel") {
            return Result.ok([
                {
                    transactions: unwrapped.flat(),
                    commitmentLevel: options.commitmentLevel
                }
            ]);
        } else if (options.order === "multiStep") {
            return Result.ok(
                unwrapped
                    .map((txns) =>
                        txns.map((txn) => ({
                            transactions: [txn],
                            commitmentLevel: options.commitmentLevel
                        }))
                    )
                    .flat()
            );
        }

        return Result.ok(
            unwrapped.slice(0, MAX_SEQUENTIAL_TRANSACTIONS).map((txn) => {
                return {
                    transactions: txn,
                    commitmentLevel: options.commitmentLevel
                };
            })
        );
    } catch (error) {
        return Result.err(error);
    }
}
